import React, { useContext, useEffect, useRef, useState } from 'react'
import { Carousel } from 'antd'
import {
    CaretRightFilled,
    LeftOutlined,
    RightOutlined,
} from '@ant-design/icons'
import { GetStarted } from '../GetStarted'
import { AccountContext } from 'core/context'
import { VideoIFrame } from 'shared/components/VideoIFrame'
import { getTranslation } from './translations'
import { useLanguage } from 'context/LanguageContext'
import { PARTICLES_PRESET, useParticlesBackground } from 'shared/hooks'
import s from './Banner.module.scss'
import 'particles.js'

/**
 * Обложка YouTube по ссылке вида .../embed/<id>?... — значение для
 * background-image: чёткая 16:9, под ней запасная (есть у любого ролика).
 */
const getYouTubeThumb = (url: string): string => {
    const match = url.match(/embed\/([^?&/]+)/)
    if (!match) return 'none'
    const base = `https://i.ytimg.com/vi/${match[1]}`
    return `url(${base}/maxresdefault.jpg), url(${base}/hqdefault.jpg)`
}

/** Ссылка на плеер с автозапуском — iframe ставится уже после нажатия Play. */
const withAutoplay = (url: string): string =>
    `${url}${url.includes('?') ? '&' : '?'}autoplay=1`

let youTubeWarmed = false
/** Заранее открывает соединения с YouTube: плеер после нажатия стартует быстрее. */
const warmYouTube = () => {
    if (youTubeWarmed) return
    youTubeWarmed = true
    ;['https://www.youtube.com', 'https://i.ytimg.com'].forEach(href => {
        const link = document.createElement('link')
        link.rel = 'preconnect'
        link.href = href
        document.head.appendChild(link)
    })
}

/** Фон первого экрана.  Заметность даём толщиной и яркостью, а не числом:
 *  нагрузку создаёт количество частиц (каждая сверяется с каждой), а
 *  нарисовать линию жирнее или точку крупнее ничего не стоит.  Поэтому
 *  частиц вдвое меньше общего пресета, но точки и линии крупнее и ярче. */
const HERO_PARTICLES = {
    ...PARTICLES_PRESET,
    particles: {
        ...PARTICLES_PRESET.particles,
        number: { ...PARTICLES_PRESET.particles.number, value: 100 },
        opacity: { value: 0.75, random: true },
        size: { value: 3.4, random: true },
        line_linked: {
            ...PARTICLES_PRESET.particles.line_linked,
            distance: 150,
            opacity: 0.55,
            width: 1.3,
        },
    },
}

/** Как часто сменяется слоган в заголовке. */
const SLOGAN_INTERVAL_MS = 10000

/** Блок с банером для главной страницы */
export const Banner: React.FC = React.memo(() => {
    const { accountData } = useContext(AccountContext)
    const { language } = useLanguage()
    const {
        bannerEyebrow,
        bannerTitles,
        bannerText,
        bannerSecondary,
        bannerNote,
        videoLinks,
    } = getTranslation(language)

    const carouselRef = useRef<React.ElementRef<typeof Carousel>>(null)
    const autoScrollRef = useRef<NodeJS.Timeout | null>(null)
    const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const isInteracted = useRef(false)

    // Index of the currently visible slide.
    const [currentSlide, setCurrentSlide] = useState(0)
    // Слайд, на котором нажали Play.  Только для него монтируется живой
    // YouTube-iframe; остальные (и все — до первого нажатия) показывают
    // лёгкую обложку-картинку, поэтому первый экран грузится быстро.
    const [playingSlide, setPlayingSlide] = useState<number | null>(null)

    // Слоган в заголовке: по кругу, пока вкладка на виду.  При «уменьшить
    // движение» остаётся первый.
    const [sloganIndex, setSloganIndex] = useState(0)
    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return
        }
        const timer = setInterval(() => {
            if (document.hidden) return
            setSloganIndex(i => i + 1)
        }, SLOGAN_INTERVAL_MS)
        return () => clearInterval(timer)
    }, [])
    const slogan = bannerTitles[sloganIndex % bannerTitles.length]
    const startAutoScroll = () => {
        // Auto-scroll is disabled to prevent looping
    }

    const stopAutoScroll = () => {
        if (autoScrollRef.current) {
            clearInterval(autoScrollRef.current)
            autoScrollRef.current = null
        }
    }

    const restartAutoScroll = () => {
        if (restartTimeoutRef.current) {
            clearTimeout(restartTimeoutRef.current)
        }
        restartTimeoutRef.current = setTimeout(() => {
            isInteracted.current = false
            startAutoScroll()
        }, 180000) // 180 seconds delay to restart auto-scroll
    }

    useParticlesBackground('particles-js', HERO_PARTICLES)

    useEffect(() => {
        startAutoScroll()

        return () => {
            stopAutoScroll()
            if (restartTimeoutRef.current) {
                clearTimeout(restartTimeoutRef.current)
            }
        }
    }, [])

    const handleUserInteraction = () => {
        isInteracted.current = true
        warmYouTube()
        stopAutoScroll()
        restartAutoScroll() // Start the timeout to restart auto-scroll after 30s
    }

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0]
        ;(e.currentTarget as HTMLElement).dataset.touchStartX =
            touch.clientX.toString()
    }

    const handleTouchMove = (e: React.TouchEvent) => {
        const touch = e.touches[0]
        const element = e.currentTarget as HTMLElement
        const touchStartX = parseFloat(element.dataset.touchStartX || '0')
        const touchEndX = touch.clientX
        const diff = touchStartX - touchEndX

        if (diff > 50) {
            // Swipe left
            if (carouselRef.current) {
                carouselRef.current.next()
            }
        } else if (diff < -50) {
            // Swipe right
            if (carouselRef.current) {
                carouselRef.current.prev()
            }
        }
    }

    // Смена слайда: iframe убираем сразу, в момент переключения, — видео
    // останавливается и не играет на фоне за следующим слайдом.
    const handleCarouselChange = (_from: number, to: number) => {
        setPlayingSlide(null)
        setCurrentSlide(to)
    }

    return (
        <div className={s.wrapper} id="bannerWrapper">
            <div id="particles-js" className={s.particles} />
            <div className={s.container}>
                {/* ─── Текст ─────────────────────────────────────── */}
                <div className={s.copy}>
                    <p className={s.eyebrow}>{bannerEyebrow}</p>

                    {/* Все слоганы лежат в одной ячейке сетки: невидимые держат
                        высоту под самый длинный, поэтому при смене текст ниже
                        не прыгает. */}
                    <div className={s.titleStack}>
                        {bannerTitles.map(text => (
                            <span
                                className={s.titleSizer}
                                key={text}
                                aria-hidden="true"
                            >
                                {text}
                            </span>
                        ))}
                        <h1 className={s.title} key={slogan}>
                            {slogan}
                        </h1>
                    </div>

                    <p className={s.text}>{bannerText}</p>

                    <div className={s.cta}>
                        {!accountData?.id && <GetStarted />}
                        <a className={s.secondary} href="#video">
                            {bannerSecondary}
                            <RightOutlined />
                        </a>
                    </div>
                    {!accountData?.id && <p className={s.note}>{bannerNote}</p>}
                </div>

                {/* ─── Видео ─────────────────────────────────────── */}
                <div
                    className={s.carouselWrapper}
                    onMouseEnter={handleUserInteraction}
                    onMouseLeave={restartAutoScroll}
                    onFocus={handleUserInteraction}
                    onBlur={restartAutoScroll}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                >
                    <div className={s.player}>
                        {/* fade: в режиме прокрутки slick клонирует крайние
                                слайды, и по нажатию Play монтировалось два
                                iframe с одним роликом (двойной звук). */}
                        <Carousel
                            dots={false}
                            effect="fade"
                            className={s.carousel}
                            ref={carouselRef}
                            beforeChange={handleCarouselChange}
                        >
                            {videoLinks.map((link, index) => (
                                <div className={s.carouselItem} key={index}>
                                    {index === playingSlide ? (
                                        <VideoIFrame
                                            className={s.video}
                                            src={withAutoplay(link)}
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            className={s.videoPoster}
                                            style={{
                                                backgroundImage:
                                                    getYouTubeThumb(link),
                                            }}
                                            onClick={() => {
                                                setPlayingSlide(index)
                                                handleUserInteraction()
                                            }}
                                            aria-label="Play video"
                                        >
                                            <span className={s.playIcon}>
                                                <CaretRightFilled />
                                            </span>
                                        </button>
                                    )}
                                </div>
                            ))}
                        </Carousel>
                    </div>

                    {/* Управление каруселью: полоски слева, стрелки справа. */}
                    <div className={s.controls}>
                        <div className={s.dotsContainer}>
                            {videoLinks.map((_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    aria-label={`Video ${index + 1}`}
                                    aria-current={currentSlide === index}
                                    className={`${s.dot} ${
                                        currentSlide === index
                                            ? s.dotActive
                                            : ''
                                    }`}
                                    onClick={() => {
                                        carouselRef.current?.goTo(index)
                                        handleUserInteraction()
                                    }}
                                />
                            ))}
                        </div>
                        <div className={s.arrows}>
                            <button
                                type="button"
                                className={s.arrow}
                                onClick={() => {
                                    carouselRef.current?.prev()
                                    handleUserInteraction()
                                }}
                                aria-label="Previous video"
                            >
                                <LeftOutlined />
                            </button>
                            <button
                                type="button"
                                className={s.arrow}
                                onClick={() => {
                                    carouselRef.current?.next()
                                    handleUserInteraction()
                                }}
                                aria-label="Next video"
                            >
                                <RightOutlined />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
})
